/**
 * InlineEditableContentWrapper — generic wrapper for inline editable content
 * in scrollable containers.
 *
 * Composes:
 *   - Lazy loading boundary: <Suspense> with a skeleton fallback
 *   - Single-edit-at-a-time coordination via InlineEditContext (optional)
 *
 * Note: Scroll-away guards are NOT rendered here — this wrapper lives outside
 * AutoSaveCoordinationProvider, so it cannot detect dirty state. Inner components
 * (e.g. InlineProposalExpanded) render their own InlineScrollAwayGuard.
 *
 * Designed to wrap React.lazy()-loaded components. The parent is responsible
 * for using React.lazy() on the children it passes.
 *
 * @example
 * ```tsx
 * const LazyEditor = React.lazy(() => import('./MyEditor'));
 *
 * <InlineEditProvider>
 *   <AutoSaveCoordinationProvider>
 *     <InlineEditableContentWrapper editId="proposal-42" skeletonHeight={160}>
 *       <LazyEditor proposalId={42} />
 *     </InlineEditableContentWrapper>
 *   </AutoSaveCoordinationProvider>
 * </InlineEditProvider>
 * ```
 */

import {
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useInlineEditContextOptional } from '@/contexts/InlineEditContext';
import { cx } from '@/utils/cx';

// ─── Component ────────────────────────────────────────────────────────────────

export interface InlineEditableContentWrapperProps {
  /** Unique identifier for this item in the InlineEditContext. */
  editId: string;
  /** Height of the skeleton placeholder shown while the lazy content loads. */
  skeletonHeight?: number | string;
  /** Lazy-loaded content to render inside the Suspense boundary. */
  children: ReactNode;
  /** Optional class name applied to the outer wrapper element. */
  className?: string;
  /** Called when requestEdit is denied so the parent can collapse/hide this item. */
  onEditDenied?: () => void;
}

/**
 * Wrapper that provides a Suspense loading boundary and scroll-away guard for
 * inline editable content. Works with InlineEditContext for single-edit-at-a-time
 * coordination (optional — degrades gracefully if no provider is found).
 */
export function InlineEditableContentWrapper({
  editId,
  skeletonHeight = 120,
  children,
  className,
  onEditDenied,
}: InlineEditableContentWrapperProps) {
  const inlineEdit = useInlineEditContextOptional();
  const [isActive, setIsActive] = useState(false);
  const [wasGranted, setWasGranted] = useState<boolean | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Track whether this wrapper holds the edit lock so we can release on unmount
  const wasGrantedRef = useRef(false);

  // Request exclusive edit access on mount
  useEffect(() => {
    if (!inlineEdit) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const granted = await inlineEdit.requestEdit(editId);
        if (!cancelled) {
          setWasGranted(granted);
          wasGrantedRef.current = granted;
          if (!granted) {
            onEditDenied?.();
          }
        }
      } catch {
        // Fail open — better to allow the edit than silently block it
        if (!cancelled) {
          setWasGranted(true);
          wasGrantedRef.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
      // Release the edit lock on unmount to prevent stale active-edit state
      if (wasGrantedRef.current) {
        inlineEdit.releaseEdit(editId);
        wasGrantedRef.current = false;
      }
    };
  }, [inlineEdit, editId, onEditDenied]);

  // Subscribe to active edit ID changes via callback pattern (no tree-wide re-renders)
  useEffect(() => {
    if (!inlineEdit) {
      return;
    }
    return inlineEdit.subscribeToActiveEdit(activeId => {
      setIsActive(activeId === editId);
    });
  }, [inlineEdit, editId]);

  // Re-acquire the edit lock when the user focuses into an already-mounted
  // wrapper that lost its active status (e.g. another proposal took the lock).
  const handleFocusCapture = useCallback(() => {
    if (!inlineEdit || !wasGrantedRef.current || isActive) {
      return;
    }
    void (async () => {
      try {
        const granted = await inlineEdit.requestEdit(editId);
        if (!granted) {
          onEditDenied?.();
        }
      } catch {
        // Fail open — same as mount-time request
      }
    })();
  }, [inlineEdit, editId, isActive, onEditDenied]);

  const skeletonStyle = useMemo(
    () => ({ height: skeletonHeight }),
    [skeletonHeight]
  );

  // No provider: render children without gating
  if (!inlineEdit) {
    return (
      <div ref={contentRef} className={cx('relative', className)}>
        <Suspense
          fallback={
            <div
              className='animate-pulse rounded-lg bg-bg-secondary'
              style={skeletonStyle}
            />
          }
        >
          {children}
        </Suspense>
      </div>
    );
  }

  // Waiting for requestEdit to resolve: show skeleton
  if (wasGranted === null) {
    return (
      <div
        className={cx('animate-pulse rounded-lg bg-bg-secondary', className)}
        style={skeletonStyle}
      />
    );
  }

  // Another edit is active and user chose "Keep Editing": still render children
  // (read-only) but skip the scroll-away guard so only the active edit is guarded.
  return (
    <div
      ref={contentRef}
      className={cx('relative', className)}
      onFocusCapture={handleFocusCapture}
    >
      <Suspense
        fallback={
          <div
            className='animate-pulse rounded-lg bg-bg-secondary'
            style={skeletonStyle}
          />
        }
      >
        {children}
      </Suspense>

      {/* Scroll-away guard is NOT rendered here — this wrapper lives outside
          AutoSaveCoordinationProvider / ExplicitSaveTrackingProvider, so it
          cannot supply hasDirtyChanges (required for the guard to fire).
          Inner components (e.g. InlineProposalExpanded) render their own
          InlineScrollAwayGuard with proper dirty-state detection. */}
    </div>
  );
}
